import Anthropic from '@anthropic-ai/sdk'
import {A11yAiProvider, PageContext, Suggestion, buildAltTextFromCaption} from '../aiSuggest'

// Minimal structural interface — real Anthropic client satisfies this, mock objects do too
type AnthropicClient = {
  messages: {
    create(
      body: Anthropic.MessageCreateParamsNonStreaming,
      options?: Anthropic.RequestOptions
    ): Promise<Anthropic.Message>
  }
}

export interface ClaudeProviderDeps {
  client?: AnthropicClient
  visionModel?: string
  textModel?: string
  timeoutMs?: number
}

const SYSTEM_PROMPT =
  "You are an accessibility specialist for GlobalIME Bank's CMS. " +
  'Write concise, accurate text for screen reader users. ' +
  'Return only the requested text — no explanations, no quotes, no markdown.'

function cleanLine(s: string): string {
  return (s || '')
    .toString()
    .trim()
    .split('\n')[0]
    .replace(/^["'`]+|["'`]+$/g, '')
    .trim()
}

function localeInstruction(locale?: string): string {
  return locale === 'ne' ? ' Respond in Nepali (Devanagari script).' : ''
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

async function toImageSource(image: Blob | string): Promise<Anthropic.ImageBlockParam['source']> {
  let dataUrl: string
  if (typeof image !== 'string') {
    dataUrl = await blobToDataUrl(image)
  } else if (image.startsWith('data:')) {
    dataUrl = image
  } else if (image.startsWith('https://')) {
    // Anthropic vision API accepts public HTTPS URLs directly
    return {type: 'url', url: image}
  } else {
    // http:// or relative URL — fetch and inline as base64 (Anthropic only accepts https:// for URL sources)
    const res = await fetch(image)
    if (!res.ok) throw new Error(`Image fetch failed: HTTP ${res.status}`)
    dataUrl = await blobToDataUrl(await res.blob())
  }
  const commaIdx = dataUrl.indexOf(',')
  if (commaIdx === -1) throw new Error(`Invalid data URL: no comma separator found`)
  const header = dataUrl.slice(0, commaIdx)
  const data = dataUrl.slice(commaIdx + 1)
  const mediaType = header
    .replace('data:', '')
    .replace(';base64', '') as Anthropic.Base64ImageSource['media_type']
  return {type: 'base64', media_type: mediaType, data}
}

const DEFAULT_TIMEOUT_MS = 30000

export function createClaudeProvider(deps: ClaudeProviderDeps = {}): A11yAiProvider {
  const visionModel =
    deps.visionModel ?? (process.env.REACT_APP_CLAUDE_VISION_MODEL ?? 'claude-sonnet-4-6')
  const textModel =
    deps.textModel ?? (process.env.REACT_APP_CLAUDE_TEXT_MODEL ?? 'claude-haiku-4-5')
  const timeoutMs = deps.timeoutMs ?? (Number(process.env.REACT_APP_ANTHROPIC_TIMEOUT_MS) || DEFAULT_TIMEOUT_MS)
  const baseURL = process.env.REACT_APP_ANTHROPIC_BASE_URL
  const client =
    deps.client ??
    new Anthropic({
      apiKey: process.env.REACT_APP_ANTHROPIC_API_KEY ?? '',
      ...(baseURL ? {baseURL} : {}),
      dangerouslyAllowBrowser: true,
    })

  const ask = async (prompt: string, model: string, maxTokens = 60): Promise<string> => {
    const msg = await client.messages.create(
      {model, max_tokens: maxTokens, system: SYSTEM_PROMPT, messages: [{role: 'user', content: prompt}]},
      {timeout: timeoutMs},
    )
    const block = msg.content[0] ?? null
    return cleanLine(block?.type === 'text' ? block.text : '')
  }

  return {
    id: 'claude',
    capabilities: {
      altText: true,
      linkText: true,
      headerLabel: true,
      tableCaption: true,
      headingHint: true,
      ariaLabel: true,
    },

    async suggestAltText(image: Blob | string, ctx?: PageContext): Promise<Suggestion> {
      const source = await toImageSource(image)
      const pageHint = ctx?.pageTitle ? ` on a page titled "${ctx.pageTitle}"` : ''
      const localeHint = localeInstruction(ctx?.locale)
      const content: Array<Anthropic.TextBlockParam | Anthropic.ImageBlockParam> = [
        {
          type: 'text',
          text:
            `Write concise WCAG alt text (max 125 characters, do not start with "image of" or "picture of") ` +
            `describing this banking image${pageHint}. Reply with only the alt text.${localeHint}`,
        },
        {type: 'image', source},
      ]
      const msg = await client.messages.create(
        {model: visionModel, max_tokens: 80, system: SYSTEM_PROMPT, messages: [{role: 'user', content}]},
        {timeout: timeoutMs},
      )
      const block = msg.content[0] ?? null
      const raw = block?.type === 'text' ? block.text : ''
      return {text: buildAltTextFromCaption(cleanLine(raw), ctx), confidence: 'high', provider: 'claude'}
    },

    async suggestLinkText(href: string, surroundingText: string, ctx?: PageContext): Promise<Suggestion> {
      const text = await ask(
        `Suggest 2-5 word action-oriented link text (e.g. "Open savings account") for a link to ${href}. ` +
          `Never use "click here" or "read more". Surrounding context: "${surroundingText}". ` +
          `Reply with only the link text.${localeInstruction(ctx?.locale)}`,
        textModel,
      )
      return {text, confidence: 'high', provider: 'claude'}
    },

    async suggestHeaderLabel(columnSamples: string[], ctx?: PageContext): Promise<Suggestion> {
      const text = await ask(
        `These are sample cells from one financial data table column: ${JSON.stringify(columnSamples)}. ` +
          `Suggest a Title Case column header label (1-3 words). Reply with only the label.${localeInstruction(ctx?.locale)}`,
        textModel,
      )
      return {text, confidence: 'high', provider: 'claude'}
    },

    async suggestTableCaption(cellSamples: string[], ctx?: PageContext): Promise<Suggestion> {
      const text = await ask(
        `These are sample cells from a table: ${JSON.stringify(cellSamples)}. ` +
          `Suggest one short phrase that helps screen reader users understand, skip, or navigate this table. ` +
          `Reply with only the caption.${localeInstruction(ctx?.locale)}`,
        textModel,
      )
      return {text, confidence: 'high', provider: 'claude'}
    },

    async suggestHeadingHint(sectionText: string, ctx?: PageContext): Promise<Suggestion> {
      const text = await ask(
        `Suggest a 2-6 word noun phrase heading (no verbs) for this content: "${sectionText.slice(0, 500)}". ` +
          `Reply with only the heading.${localeInstruction(ctx?.locale)}`,
        textModel,
      )
      return {text, confidence: 'high', provider: 'claude'}
    },

    async suggestAriaLabel(elementDescription: string, ctx?: PageContext): Promise<Suggestion> {
      const text = await ask(
        `Suggest a concise aria-label for this UI element on a banking website: "${elementDescription}". ` +
          `Use imperative form if it is an action element. Reply with only the label.${localeInstruction(ctx?.locale)}`,
        textModel,
      )
      return {text, confidence: 'high', provider: 'claude'}
    },
  }
}
